/**
 * Chat Patterns and Response Templates for Vehicle AI Assistant
 * Provides intent matching and context-aware response generation
 */

export const chatPatterns = {
  // Greetings and Conversational
  greeting: {
    patterns: [/^(hi|hello|hey|good morning|good afternoon|good evening)/i],
    responses: [
      "Hello! I'm your EV assistant. How can I help you today?",
      "Hi there! Ready to assist with any questions about your vehicle.",
      "Hello! I'm here to help with battery status, diagnostics, or any vehicle queries."
    ]
  },

  thanks: {
    patterns: [/(thank you|thanks|appreciate)/i],
    responses: [
      "You're welcome! Feel free to ask anything else about your vehicle.",
      "Happy to help! Let me know if you need anything else.",
      "My pleasure! I'm here whenever you need vehicle assistance."
    ]
  },

  help: {
    patterns: [/(help|what can you do|capabilities|features)/i],
    responses: [
      "I can help you with:\n• Battery status and health\n• Range estimates\n• Diagnostic codes and warnings\n• Motor performance\n• Charging information\n• Maintenance schedules\n• Safety protocols\n\nJust ask me anything!"
    ]
  },

  // Battery Related
  batteryStatus: {
    patterns: [
      /(battery|charge|soc|state of charge)(?!.*temp)/i,
      /how much (charge|battery)/i,
      /battery (status|level|percentage)/i
    ],
    generateResponse: (vehicleData) => {
      const soc = vehicleData?.battery?.soc || 87;
      const soh = vehicleData?.battery?.soh || 96;
      const range = typeof vehicleData?.battery?.range === 'object'
        ? vehicleData?.battery?.range?.estimated
        : vehicleData?.battery?.range || 312;

      let response = `Your battery is at **${soc.toFixed(1)}%** charge`;

      if (soc > 80) {
        response += ", which is excellent! ";
      } else if (soc > 50) {
        response += ", good for city driving. ";
      } else if (soc > 20) {
        response += ". Consider charging soon. ";
      } else {
        response += ". ⚠️ Low charge - please charge immediately. ";
      }

      response += `You have approximately **${range} km** of range remaining. `;
      response += `Battery health is at **${soh}%**, which is ${soh > 90 ? 'excellent' : soh > 80 ? 'good' : 'fair'}.`;

      return response;
    }
  },

  range: {
    patterns: [
      /how (far|much|many) (can i|distance|km)/i,
      /range|distance (left|remaining)/i,
      /can i (drive|go|travel)/i
    ],
    generateResponse: (vehicleData) => {
      const range = typeof vehicleData?.battery?.range === 'object'
        ? vehicleData?.battery?.range?.estimated
        : vehicleData?.battery?.range || 312;
      const soc = vehicleData?.battery?.soc || 87;

      let response = `Based on your current **${soc.toFixed(1)}%** charge, you can drive approximately **${range} km**. `;

      if (range > 250) {
        response += "That's plenty for most trips! ";
      } else if (range > 150) {
        response += "Good for city commutes and moderate trips. ";
      } else if (range > 50) {
        response += "⚠️ Plan to charge soon for longer trips. ";
      } else {
        response += "🚨 Low range - charge as soon as possible!";
      }

      return response;
    }
  },

  charging: {
    patterns: [
      /charg(e|ing)/i,
      /when (did i|was|last) charge/i,
      /how long to charge/i
    ],
    generateResponse: (vehicleData) => {
      const lastCharge = vehicleData?.battery?.lastCharge;
      const soc = vehicleData?.battery?.soc || 87;

      let response = "";

      if (lastCharge) {
        const hoursAgo = Math.round((Date.now() - lastCharge.timestamp) / (1000 * 60 * 60));
        response += `You last charged **${hoursAgo} hours ago** to **${lastCharge.maxLevel}%** using ${lastCharge.type} charging. `;
      }

      const chargeNeeded = 100 - soc;
      if (chargeNeeded > 0) {
        const acTime = Math.round(chargeNeeded / 100 * 6 * 100) / 100; // ~6 hours for full
        const dcTime = Math.round(chargeNeeded / 100 * 45); // ~45 mins for 80%
        response += `\n\nTo fully charge:\n• **AC (3.3kW)**: ~${acTime} hours\n• **DC Fast**: ~${dcTime} minutes to 80%`;
      }

      return response;
    }
  },

  // Temperature and Thermal
  temperature: {
    patterns: [
      /(temp|temperature|hot|cold|heat)/i,
      /battery.*temp/i,
      /motor.*temp/i
    ],
    generateResponse: (vehicleData) => {
      const battTemp = vehicleData?.battery?.temperature?.pack || 28.5;
      const motorTemp = vehicleData?.motor?.temperature || 65;

      let response = `**Battery temperature**: ${battTemp.toFixed(1)}°C `;
      response += battTemp < 35 ? "(Normal ✅)" : battTemp < 45 ? "(Warm ⚠️)" : "(High 🚨)";
      response += `\n**Motor temperature**: ${motorTemp.toFixed(1)}°C `;
      response += motorTemp < 80 ? "(Normal ✅)" : motorTemp < 100 ? "(Warm ⚠️)" : "(High 🚨)";

      if (battTemp > 40 || motorTemp > 90) {
        response += "\n\n⚠️ **Recommendation**: Avoid fast charging and aggressive acceleration until temperatures normalize.";
      }

      return response;
    }
  },

  // Motor and Performance
  motor: {
    patterns: [
      /motor|engine/i,
      /performance|power|torque/i,
      /acceleration/i
    ],
    generateResponse: (vehicleData) => {
      const motor = vehicleData?.motor;
      const temp = motor?.temperature || 65;
      const efficiency = motor?.efficiency || 94.2;
      const status = motor?.status || 'idle';

      let response = `**Motor Status**: ${status.toUpperCase()}\n`;
      response += `**Temperature**: ${temp.toFixed(1)}°C ${temp < 80 ? '✅' : '⚠️'}\n`;
      response += `**Efficiency**: ${efficiency.toFixed(1)}%\n`;
      response += `**Specs**: ${motor?.specs?.maxPower || 129}kW / ${motor?.specs?.maxTorque || 245}Nm\n\n`;

      if (status === 'idle') {
        response += "Motor is in great condition and ready for your drive!";
      } else {
        response += `Motor is ${status} with excellent cooling.`;
      }

      return response;
    }
  },

  // Diagnostics and Warnings
  warnings: {
    patterns: [
      /(warning|alert|error|dtc|diagnostic|trouble|code|p1a08)/i,
      /(problem|issue|fault)/i,
      /any (error|warning|problem)/i
    ],
    generateResponse: (vehicleData) => {
      const alerts = vehicleData?.alerts || [];

      if (alerts.length === 0) {
        return "🎉 **No warnings detected!** All systems are operating normally. Your vehicle is in excellent condition.";
      }

      let response = `⚠️ **${alerts.length} active warning${alerts.length > 1 ? 's' : ''}**:\n\n`;

      alerts.forEach((alert, idx) => {
        response += `${idx + 1}. **${alert.id}**: ${alert.title}\n`;
        response += `   ${alert.description}\n`;
        response += `   Status: ${alert.status}\n\n`;
      });

      response += "💡 Click the **ALERTS** panel for detailed troubleshooting steps.";

      return response;
    }
  },

  dtcExplanation: {
    patterns: [/p1a08|temperature sensor/i],
    responses: [
      "**P1A08** - Battery Temperature Sensor Discrepancy\n\n" +
      "**What it means**: Section 2 temperature sensor is reading slightly higher than other sensors.\n\n" +
      "**Is it safe?** YES ✅ - All cells are within safe temperature ranges. This is likely just a sensor calibration issue.\n\n" +
      "**Action**: Vehicle is safe to drive. Have it checked at your next service appointment.\n\n" +
      "**Technical**: This code triggers when one temperature sensor reading deviates >3°C from the pack average, but actual cell temperatures are normal."
    ]
  },

  // Safety and Maintenance
  safety: {
    patterns: [
      /(safe|danger|high voltage|hv|electric shock)/i,
      /safety (procedure|protocol|warning)/i
    ],
    responses: [
      "⚠️ **HIGH VOLTAGE SAFETY PROTOCOL**\n\n" +
      "This vehicle operates at 400V - potentially lethal. For ANY work on high voltage components:\n\n" +
      "**CRITICAL STEPS (in order)**:\n" +
      "1. ⚡ Disconnect 12V battery\n" +
      "2. 🔌 Remove HV service plug (orange connector)\n" +
      "3. ⏱️ Wait 10 minutes (capacitor discharge)\n" +
      "4. 🧤 Wear Class 0 insulating gloves (1000V rated)\n" +
      "5. ⚠️ Use insulated tools only\n" +
      "6. 📋 Follow service manual procedures\n\n" +
      "**⚠️ NEVER work on HV system without proper training and certification!**\n\n" +
      "Orange cables = HIGH VOLTAGE - Do not touch!"
    ]
  },

  maintenance: {
    patterns: [
      /(service|maintenance|due)/i,
      /when.*service/i,
      /next.*maintenance/i
    ],
    generateResponse: (vehicleData) => {
      const odo = vehicleData?.vehicle?.odometer || 8547;
      const nextService = Math.ceil(odo / 10000) * 10000;
      const remaining = nextService - odo;

      let response = `**Current odometer**: ${odo.toLocaleString()} km\n`;
      response += `**Next service**: ${nextService.toLocaleString()} km (in **${remaining} km**)\n\n`;
      response += "**Service includes**:\n";
      response += "• Brake system inspection\n";
      response += "• Tire rotation and pressure check\n";
      response += "• Coolant level check (battery & motor)\n";
      response += "• HV connector inspection\n";
      response += "• Software updates\n";
      response += "• Multi-point vehicle inspection";

      if (remaining < 500) {
        response += "\n\n⚠️ **Service due soon!** Book your appointment.";
      }

      return response;
    }
  },

  // Location and Trip
  location: {
    patterns: [/(where am i|location|parked)/i],
    generateResponse: (vehicleData) => {
      const location = vehicleData?.vehicle?.location;
      return `📍 Your vehicle is currently in **${location?.city || 'Pune'}**, India.`;
    }
  },

  // Health and Degradation
  health: {
    patterns: [
      /(health|condition|degradation)/i,
      /battery.*health/i,
      /soh/i
    ],
    generateResponse: (vehicleData) => {
      const soh = vehicleData?.battery?.soh || 96;
      const cycles = vehicleData?.battery?.cycles || 487;
      const degradation = vehicleData?.battery?.health?.degradation || 4;
      const yearsRemaining = vehicleData?.battery?.health?.estimatedLife || 8;

      let response = `**Battery Health (SOH)**: ${soh}% `;
      response += soh > 95 ? "(Excellent ✅)" : soh > 85 ? "(Very Good 👍)" : soh > 75 ? "(Good 👌)" : "(Fair ⚠️)";
      response += `\n**Degradation**: ${degradation}% from new\n`;
      response += `**Charge Cycles**: ${cycles}\n`;
      response += `**Estimated Life**: ${yearsRemaining} years remaining\n\n`;

      if (soh > 90) {
        response += "Your battery is in excellent condition! Continue following best practices for optimal longevity.";
      }

      return response;
    }
  },

  // Unknown/Fallback
  unknown: {
    responses: [
      "I'm not sure I understand. Could you rephrase? I can help with battery status, diagnostics, maintenance, and more.",
      "I didn't quite catch that. Try asking about battery, range, warnings, or motor status.",
      "I'm here to help with your vehicle. Try asking about:\n• Battery and range\n• Warnings or errors\n• Motor performance\n• Maintenance schedule\n• Safety protocols"
    ]
  }
};

// Quick action suggestions - Vehicle specific
export const quickActionsByVehicle = {
  nexon: [
    { id: 'components', label: 'All Components', icon: '🔧', query: 'What are the main components of the light commercial EV?' },
    { id: 'charger', label: 'Charger Failures', icon: '🔌', query: 'What are the failure modes of the charger?' },
    { id: 'battery', label: 'Battery Cooling', icon: '🔋', query: 'How does the battery cooling system work?' },
    { id: 'canbus', label: 'CAN Bus', icon: '📡', query: 'What is the CAN Bus architecture?' },
    { id: 'range', label: 'Range & DTE', icon: '📍', query: 'What causes range and DTE issues?' },
    { id: 'telematics', label: 'Telematics', icon: '📊', query: 'What are the telematics system specifications?' },
    { id: 'electrical', label: 'Electrical', icon: '⚡', query: 'What are the electrical system components?' },
    { id: 'chassis', label: 'Chassis', icon: '🚗', query: 'What are the chassis specifications?' },
  ],
  nano: [
    { id: 'ecu', label: 'ECU Info', icon: '🖥️', query: 'What is the ECU?' },
    { id: 'engine', label: 'Engine Specs', icon: '⚙️', query: 'What are the engine specifications?' },
    { id: 'sensors', label: 'Sensors', icon: '📡', query: 'What sensors does the city car have?' },
    { id: 'relays', label: 'Relays', icon: '🔌', query: 'What relays are in the city car?' },
    { id: 'electrical', label: 'Electrical System', icon: '⚡', query: 'Tell me about the electrical system' },
    { id: 'temperature_sensor', label: 'Temp Sensor', icon: '🌡️', query: 'Tell me about the temperature sensor' },
  ],
  compressor: [
    { id: 'safety', label: 'Safety Procedures', icon: '⚠️', query: 'What are the safety procedures for maintenance?' },
    { id: 'oil_change', label: 'Oil Change', icon: '🛢️', query: 'How do I change the oil?' },
    { id: 'maintenance', label: 'Maintenance Schedule', icon: '📅', query: 'What is the maintenance schedule?' },
    { id: 'troubleshooting', label: 'Troubleshooting', icon: '🔧', query: 'How do I troubleshoot high temperature warning?' },
    { id: 'specs', label: 'Specifications', icon: '📊', query: 'What are the technical specifications?' },
    { id: 'controller', label: 'Controller', icon: '🖥️', query: 'Tell me about the controller' },
  ]
};

// Legacy export for backward compatibility (defaults to the light commercial EV)
export const quickActions = quickActionsByVehicle.nexon;
